
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2, Pencil } from 'lucide-react';
import { useTeacherDetail } from '@/hooks/useTeacherDetail';
import { TeacherDetailProfile } from '@/components/admin/teacher-detail/TeacherDetailProfile';
import { TeacherMonthlyReports } from '@/components/admin/teacher-detail/TeacherMonthlyReports';
import { TeacherAvailabilitySchedule } from '@/components/admin/teacher-detail/TeacherAvailabilitySchedule';
import { TeacherAvailabilityExceptions } from '@/components/admin/teacher-detail/TeacherAvailabilityExceptions';
import { AdminSidebar } from '@/components/admin/AdminSidebar';
import { SidebarProvider } from '@/components/ui/sidebar';

const AdminTeacherDetailPage: React.FC = () => {
  const { teacherCode } = useParams<{ teacherCode: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = useTeacherDetail(teacherCode!);

  const handleBackClick = () => {
    navigate('/admin/teachers');
  };

  const wrapWithSidebar = (content: React.ReactNode) => (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <AdminSidebar />
        <main className="flex-1 overflow-auto">
          {content}
        </main>
      </div>
    </SidebarProvider>
  );

  if (isLoading) {
    return wrapWithSidebar(
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return wrapWithSidebar(
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="py-8">
            <div className="text-center text-red-600">
              เกิดข้อผิดพลาดในการโหลดข้อมูล: {error.message}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data?.profile) {
    return wrapWithSidebar(
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="py-8">
            <div className="text-center text-gray-600">
              ไม่พบข้อมูลครู
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return wrapWithSidebar(
    <div className="container mx-auto px-4 py-8">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={handleBackClick}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              กลับ
            </Button>
            <div>
              <h1 className="text-2xl font-bold">ข้อมูลครู</h1>
              <p className="text-muted-foreground">
                {data.profile.teacher_code} - {data.profile.full_name}
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate(`/admin/teachers/${teacherCode}/edit`)}>
            <Pencil className="h-4 w-4 mr-2" />แก้ไข
          </Button>
        </div>

        {/* Teacher Profile */}
        <TeacherDetailProfile profile={data.profile} />

        {/* Monthly Reports */}
        <TeacherMonthlyReports reports={data.monthlyReports} />

        {/* Availability Schedule */}
        <TeacherAvailabilitySchedule templates={data.availabilityTemplates} />

        {/* Availability Exceptions */}
        <TeacherAvailabilityExceptions exceptions={data.availabilityExceptions} />
      </div>
    </div>
  );
};

export default AdminTeacherDetailPage;
